import React, { ReactElement } from 'react';
import urljoin from 'url-join';

import Config from '../lib/config';

import { FeedPage, Dimensions, FeedArea } from '../../shared/types/feedTemplate';
import {
  BareContentTemplateLegacy,
  ContentTemplateParams,
  FeedEditorElementType,
} from '../../shared/types/contentTemplate';
import { FeedEditorHotspot } from '.';
import { Time } from '../../shared/types/time';
// import { makeEventSorter } from '../../shared/lib/event';
import { BareSailingDay } from '../../shared/types/sailing';
import {
  buildEventContentTemplateFromEvent,
  buildVenueContentTemplateFromVenue,
} from '@go-client/lib/api/feedTemplate';
import { Property } from '@go-shared/types/property';
import { VenueTemplate } from '@go-shared/types/venueTemplate';
import { Plan } from '@go-shared/types/plan';
import { singleVenueDispositionMapper } from '@go-shared/lib/mealPeriods';
import { venueDispositionContent } from './FeedEditor/ContentSidebar/Blurbs/VenueBlurbs';
import { VenuesRundownHotspot } from './FeedEditor/VenueRundown/VenueRundownHotspot';
import { VenueMealPeriodsHotspot } from './FeedEditor/VenueMealPeriods/VenueMealPeriodsHotspot';
import { NewLayoutComponentActions } from './FeedEditorHotspot';
import { FeedEditorCtx } from '@go-client/context/FeedEdtitorContext';
import { Language } from '@go-shared/types/info';
import { DailyProgram, DailyProgramEvent } from '@go-shared/types/dailyProgram';

export interface InteractiveFeedProps {
  zoom: number;
  mcmKey: string;
  selectedLanguage: Language | undefined;
  showMergedVars: boolean;
  highlightAreas: boolean;
  dimensions: Dimensions;
  page: FeedPage;
  contentTemplates: BareContentTemplateLegacy[];
  onContentChange?: (
    area: FeedArea,
    contentTemplate: BareContentTemplateLegacy,
    contentTemplateParams?: ContentTemplateParams
  ) => void;
  dailyProgram: DailyProgram;
  dayStart: Time;
  sailingDay?: BareSailingDay;
  property: Property;
  venueTemplates: VenueTemplate[];
  plan: Plan;
  cssBundle: string;
  onRowCreated: (parentArea: FeedArea, idx: number, newArea: FeedArea) => void;
  onColumnCreated: (parentArea: FeedArea, idx: number, newArea: FeedArea) => void;
  onRowMove: (parentArea: FeedArea, idx: number, direction: 1 | -1) => void;
  onElementCreated: (area: FeedArea, elementType: FeedEditorElementType) => void;
  onAreaDeleted: (area: FeedArea) => void;
  onElementChanged: (area: FeedArea) => void;
  activeElement?: string;
  setActiveElement?: (id: string) => void;
  editorLocked: boolean;
  simulateMargins: boolean;
}

// FIXME FIXME FIXME: Date format for MSC, it's repeated in server/lib/feed.ts
function getMSCDateFormat(date: Date) {
  let weekDay = new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', weekday: 'long' }).format(date);
  let month = new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', month: 'long' }).format(date);
  return `${weekDay}, ${date.getUTCDate()} ${month} ${date.getUTCFullYear()}`;
}

export default class InteractiveFeed extends React.Component<InteractiveFeedProps> {
  // private eventSorter: (e1: Event, e2: Event) => number;

  // private dailyProgramEventSorter: (e1: DailyProgramEvent, e2: DailyProgramEvent) => number;

  constructor(props: InteractiveFeedProps) {
    super(props);

    // this.eventSorter = makeEventSorter(timeToMinutes(props.dayStart));
  }

  public render() {
    let backgroundImage = `${Config.PUBLIC_ASSETS_URL}/feeds/${this.props.page.image}`;

    const zoomClamp = Math.min(Math.max(this.props.zoom, 1), 20);

    let wrapperStyle = {
      backgroundImage: `url(${backgroundImage})`,
      backgroundRepeat: 'no-repeat',
      width: this.props.dimensions.width,
      height: this.props.dimensions.height,
      margin: Math.max(10 + (zoomClamp - 10) * 20, 10) + '%',
      transform: `scale(${zoomClamp / 10})`,
    };

    if (this.props.simulateMargins) {
      (wrapperStyle as any).border = '25px solid white';
      (wrapperStyle as any).boxSizing = 'content-box';
    }

    // TODO: Move this sorting inside
    // let events = Array.from(this.props.events).sort(this.eventSorter);

    return (
      <FeedEditorCtx.Consumer>
        {_value => (
          <div className="relative bg-contain shadow-xl" style={wrapperStyle}>
            {this.props.page.areas.map(area => {
              let contentTemplate = area.content
                ? this.props.contentTemplates.find(c => c._id === area.content!._id)
                : undefined;

              return (
                <React.Fragment key={area._id}>
                  {this.contentAreaToElement(
                    area,
                    this.props.dailyProgram,
                    this.props.highlightAreas,
                    this.props.selectedLanguage,
                    this.props.showMergedVars,
                    contentTemplate,
                    this.props.sailingDay
                  )}
                </React.Fragment>
              );
            })}
          </div>
        )}
      </FeedEditorCtx.Consumer>
    );
  }

  public async componentDidMount() {
    // @ts-ignore
    if (this.props.cssBundle) {
      return import(
        /* webpackChunkName: "[request]" */ `../styles/feeds/${this.props.cssBundle}.scss`
      );
    }
  }

  public async componentDidUpdate(prevProps: Readonly<InteractiveFeedProps>) {
    if (prevProps.cssBundle !== this.props.cssBundle) {
      this.removeCssBundleElement(prevProps.cssBundle);
    }

    if (this.props.cssBundle) {
      // @ts-ignore
      await import(
        /* webpackChunkName: "[request]" */ `../styles/feeds/${this.props.cssBundle}.scss`
      );

      this.ensureCssBundleElement(this.props.cssBundle);
    }
  }

  private removeCssBundleElement(cssBundle: string) {
    const links = document.getElementsByTagName('link');
    const hrefToRemove = `/styles-${cssBundle}-scss.css`;

    for (let i = 0; i < links.length; i++) {
      const link = links[i];
      const href = link.getAttribute('href');

      if (href === hrefToRemove) {
        link.parentNode?.removeChild(link);
      }
    }
  }

  private ensureCssBundleElement(cssBundle: string) {
    const links = document.getElementsByTagName('link');
    const hrefToEnsure = `/styles-${cssBundle}-scss.css`;

    for (let i = 0; i < links.length; i++) {
      const link = links[i];
      const href = link.getAttribute('href');

      if (href === hrefToEnsure) {
        return;
      }
    }

    const link = document.createElement('link');
    link.setAttribute('rel', 'stylesheet');
    link.setAttribute('type', 'text/css');
    link.setAttribute('href', hrefToEnsure);
    document.head.appendChild(link);
  }

  private contentAreaToElement(
    area: FeedArea,
    dailyProgram: DailyProgram,
    highlightAreas: boolean,
    selectedLanguage: Language | undefined,
    showMergedVars: boolean,
    contentTemplate?: BareContentTemplateLegacy,
    bareSailingDay?: BareSailingDay
  ): ReactElement<any> {
    // TODO: merge with feededitorhotspot
    const feedEditorHotspotActions: NewLayoutComponentActions = {
      onColumnCreated: this.props.onColumnCreated,
      onRowMove: this.props.onRowMove,
      onRowCreated: this.props.onRowCreated,
      onElementCreated: this.props.onElementCreated,
      onAreaDeleted: this.props.onAreaDeleted,
      onElementChanged: this.props.onElementChanged,
    };

    let contentTemplateReady: BareContentTemplateLegacy | undefined;
    //TODO: Apply sorting to the daily program events
    switch (area.contentType) {
      case 'event':
        let event: DailyProgramEvent | undefined;
        if (area.params && area.params.eventId) {
          event = dailyProgram.events.find(ev => ev.templateId === area.params!.eventId);
        }
        if (event) {
          contentTemplateReady = buildEventContentTemplateFromEvent(
            event,
            this.props.property,
            e => `<div>
          <h1>${e.title}</h1>
          <p>${e.description}</p>
        </div>`
          );
        }

        return (
          <FeedEditorHotspot
            key={area._id}
            area={area}
            {...feedEditorHotspotActions}
            contentTemplate={contentTemplateReady}
            showMergedVars={this.props.showMergedVars}
            highlightAreas={this.props.highlightAreas}
            selectedLanguage={this.props.selectedLanguage}
            onContentChange={this.props.onContentChange!}
            onDrop={
              this.props.onContentChange ? ct => this.props.onContentChange!(area, ct) : undefined
            }
            editorLocked={this.props.editorLocked}
            mcmKey={this.props.mcmKey}
          />
        );

      case 'venue-rundown':
        return (
          <>
            {
              <VenuesRundownHotspot
                onAreaDeleted={this.props.onAreaDeleted}
                key={area._id}
                area={area}
                contentTemplate={{
                  _id: area._id,
                  name: '',
                  content: '',
                  params: area.params,
                  property: this.props.property._id,
                }}
                selectedLanguage={selectedLanguage}
                showMergedVars={showMergedVars}
                highlightAreas={highlightAreas}
                editorLocked={this.props.editorLocked}
                onDrop={
                  this.props.onContentChange
                    ? ct => this.props.onContentChange!(area, ct)
                    : undefined
                }
                commonTexts={dailyProgram.collections.commonTexts}
                mcmKey={this.props.mcmKey}
              />
            }
          </>
        );

      case 'venue-meal-periods':
        return (
          <>
            {
              <VenueMealPeriodsHotspot
                onAreaDeleted={this.props.onAreaDeleted}
                key={area._id}
                area={area}
                contentTemplate={{
                  _id: area._id,
                  name: '',
                  content: '',
                  params: area.params,
                  property: this.props.property._id,
                }}
                selectedLanguage={selectedLanguage}
                showMergedVars={showMergedVars}
                highlightAreas={highlightAreas}
                editorLocked={this.props.editorLocked}
                onDrop={
                  this.props.onContentChange
                    ? ct => this.props.onContentChange!(area, ct)
                    : undefined
                }
                commonTexts={dailyProgram.collections.commonTexts}
                mcmKey={this.props.mcmKey}
              />
            }
          </>
        );

      case 'venue':
        let venue: VenueTemplate | undefined;
        if (area.params && area.params.venueId) {
          venue = this.props.venueTemplates.find(v => v._id === area.params!.venueId);
        }

        if (venue) {
          contentTemplateReady = buildVenueContentTemplateFromVenue(
            venue,
            this.props.property,
            _ => `<div>
            <h1>${venue!.name}</h1>
            ${venueDispositionContent(singleVenueDispositionMapper(this.props.plan, venue!))}
          </div>`
          );
        }

        return (
          <FeedEditorHotspot
            {...feedEditorHotspotActions}
            key={area._id}
            area={area}
            contentTemplate={contentTemplateReady}
            onContentChange={this.props.onContentChange!}
            showMergedVars={this.props.showMergedVars}
            highlightAreas={this.props.highlightAreas}
            selectedLanguage={this.props.selectedLanguage}
            onDrop={
              this.props.onContentChange ? ct => this.props.onContentChange!(area, ct) : undefined
            }
            editorLocked={this.props.editorLocked}
            mcmKey={this.props.mcmKey}
          />
        );
      case 'static':
        return (
          <>
            <FeedEditorHotspot
              {...feedEditorHotspotActions}
              key={area._id}
              area={area}
              selectedLanguage={this.props.selectedLanguage}
              showMergedVars={this.props.showMergedVars}
              highlightAreas={this.props.highlightAreas}
              contentTemplate={contentTemplate}
              onContentChange={this.props.onContentChange!}
              onDrop={
                this.props.onContentChange ? ct => this.props.onContentChange!(area, ct) : undefined
              }
              activeElem={this.props.activeElement}
              setActiveElement={this.props.setActiveElement}
              editorLocked={this.props.editorLocked}
              mcmKey={this.props.mcmKey}
            />
          </>
        );
      case 'location-image':
        let locationImage;

        if (this.props.sailingDay?.type === 'sea') {
          locationImage = 'sea.svg';
        } else {
          // WARNING: This only uses the first port
          locationImage =
            this.props.sailingDay && this.props.sailingDay.locations.length > 0
              ? this.props.sailingDay.locations[0].name.toLowerCase().replace(/[ ]+/g, '') + '.svg'
              : 'sea.svg';
        }

        locationImage = urljoin(Config.PUBLIC_ASSETS_URL, '/locations', locationImage);

        return (
          <div
            key={area._id}
            className="absolute"
            style={{
              top: area.rectangle.y,
              left: area.rectangle.x,
              width: area.rectangle.width,
              height: area.rectangle.height,
            }}
          >
            <img src={locationImage} />
          </div>
        );
      case 'date':
        return (
          <div
            key={area._id}
            className={`ContentBlock absolute overflow-hidden ${area.className} ${
              selectedLanguage ? `lang-${selectedLanguage.key}` : ''
            }`}
            style={Object.assign({}, area.style, {
              top: area.rectangle.y,
              left: area.rectangle.x,
              width: area.rectangle.width,
              height: area.rectangle.height,
            })}
          >
            {getMSCDateFormat(bareSailingDay?.date || new Date())}
          </div>
        );
    }
    return <React.Fragment key={area._id}></React.Fragment>;
  }
}
